import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchServicePackages } from '../services/supabaseService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { createOrder, formatIDR } from '../services/portalService';
import { TickSquare, CloseSquare } from 'react-iconly';
import '../portal/portal.css';
import './Pricing.css';

const PLANS = [
  { id: 'full', factor: 1 },
  { id: 'dp50', factor: 0.5 },
  { id: 'milestone', factor: null },
];

const CATEGORY_LABELS = { web: 'Web', mobile: 'Mobile', design: 'Design' };

const Pricing = () => {
  const navigate = useNavigate();
  const { user, signInWithGoogle } = useAuth();
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('all');
  const [modalPkg, setModalPkg] = useState(null);
  const [plan, setPlan] = useState('full');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const planLabels = {
    full: { title: t('pricing.payInFull'), desc: t('pricing.payInFullDesc') },
    dp50: { title: t('pricing.dp50'), desc: t('pricing.dp50Desc') },
    milestone: { title: t('pricing.milestone'), desc: t('pricing.milestoneDesc') },
  };

  useEffect(() => {
    fetchServicePackages().then((data) => {
      setPackages(data || []);
      setLoading(false);
    });
  }, []);

  const beginOrder = useCallback(async (pkg, chosenPlan) => {
    const order = await createOrder({ pkg, plan: chosenPlan, total: pkg.price });
    navigate(`/dashboard?order=${order.id}`);
  }, [navigate]);

  // Resume an order started before a Google sign-in redirect.
  useEffect(() => {
    if (!user || loading) return;
    const pending = sessionStorage.getItem('pendingOrder');
    if (!pending) return;
    try {
      const { pkgId, plan: p } = JSON.parse(pending);
      const pkg = packages.find((x) => String(x.id) === String(pkgId));
      sessionStorage.removeItem('pendingOrder');
      if (pkg) beginOrder(pkg, p || 'full');
    } catch (_) { sessionStorage.removeItem('pendingOrder'); }
  }, [user, loading, packages, beginOrder]);

  const categories = Array.from(new Set(packages.map((p) => p.category).filter(Boolean)));
  const visible = activeCat === 'all' ? packages : packages.filter((p) => p.category === activeCat);

  const openModal = (pkg) => { setModalPkg(pkg); setPlan('full'); setError(''); };

  const confirmOrder = async () => {
    if (!modalPkg) return;
    setBusy(true); setError('');
    try {
      if (!user) {
        sessionStorage.setItem('pendingOrder', JSON.stringify({ pkgId: modalPkg.id, plan }));
        await signInWithGoogle('/pricing');
        return; // redirects away
      }
      await beginOrder(modalPkg, plan);
    } catch (e) {
      setError(e.message || 'Could not start your order');
      setBusy(false);
    }
  };

  const parseFeatures = (raw) => {
    try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || []); }
    catch { return []; }
  };

  return (
    <div className="pricing">
      <div className="pricing-inner">
        <header className="pricing-hero">
          <p className="portal-eyebrow">{t('pricing.eyebrow')}</p>
          <h1 className="pricing-h1">{t('pricing.title')}</h1>
          <p className="pricing-lead">
            {t('pricing.subtitle')}
          </p>
          <div className="pricing-plan-strip">
            {PLANS.map((p) => (
              <div key={p.id} className="pricing-plan-chip">
                <span className="pricing-plan-chip-title">{planLabels[p.id].title}</span>
                <span className="pricing-plan-chip-desc">{planLabels[p.id].desc}</span>
              </div>
            ))}
          </div>
        </header>

        {categories.length > 1 && (
          <div className="portal-tabs" style={{ width: 'fit-content', margin: '0 auto 36px' }}>
            <button className={`portal-tab ${activeCat === 'all' ? 'active' : ''}`} onClick={() => setActiveCat('all')}>{t('pricing.all')}</button>
            {categories.map((c) => (
              <button key={c} className={`portal-tab ${activeCat === c ? 'active' : ''}`} onClick={() => setActiveCat(c)}>
                {CATEGORY_LABELS[c] || c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="portal-spinner" />
        ) : visible.length === 0 ? (
          <div className="portal-empty">
            <div className="portal-empty-emoji">📦</div>
            <h3 style={{ fontFamily: 'var(--v2-font-display)' }}>{t('pricing.noPackages')}</h3>
            <p className="portal-muted">{t('pricing.noPackagesDesc')}</p>
          </div>
        ) : (
          <div className="pricing-grid">
            {visible.map((pkg) => {
              const features = parseFeatures(pkg.features);
              return (
                <div key={pkg.id} className={`pricing-card ${pkg.popular ? 'popular' : ''}`}>
                  {pkg.popular && <div className="pricing-pop">{t('pricing.mostPopular')}</div>}
                  {pkg.category && <span className="pricing-cat">{CATEGORY_LABELS[pkg.category] || pkg.category}</span>}
                  <h3 className="pricing-name">{pkg.name}</h3>
                  <div className="pricing-price">
                    {pkg.price ? formatIDR(pkg.price) : t('pricing.custom')}
                    {pkg.price ? <span className="pricing-price-unit">{t('pricing.perProject')}</span> : null}
                  </div>
                  {pkg.description && <p className="pricing-desc">{pkg.description}</p>}
                  <ul className="pricing-features">
                    {features.map((f, i) => {
                      const excluded = typeof f === 'object' && f && f.included === false;
                      const label = typeof f === 'object' ? (f.text || f.label || '') : f;
                      return (
                        <li key={i} className={excluded ? 'excluded' : ''}>
                          {excluded ? <CloseSquare set="bulk" size={18} /> : <TickSquare set="bold" size={18} />}
                          <span>{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <button
                    className={`portal-btn portal-btn-block ${pkg.popular ? 'portal-btn-primary' : 'portal-btn-dark'}`}
                    onClick={() => (pkg.price ? openModal(pkg) : navigate('/contact'))}
                  >
                    {pkg.price ? t('pricing.startProject') : t('pricing.contactUs')}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalPkg && (
        <div className="pricing-modal-backdrop" onClick={() => !busy && setModalPkg(null)}>
          <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pricing-modal-close" onClick={() => !busy && setModalPkg(null)} aria-label="Close">✕</button>
            <p className="portal-eyebrow">{t('pricing.startYourProject')}</p>
            <h2 style={{ fontFamily: 'var(--v2-font-display)', fontSize: 26, margin: '0 0 4px' }}>{modalPkg.name}</h2>
            <p className="portal-muted" style={{ marginBottom: 18 }}>{t('pricing.projectTotal')} {formatIDR(modalPkg.price)}</p>

            <p className="portal-label">{t('pricing.chooseHowToPay')}</p>
            <div className="plan-options">
              {PLANS.map((p) => {
                const amount = p.factor != null ? modalPkg.price * p.factor : null;
                return (
                  <label key={p.id} className={`plan-opt ${plan === p.id ? 'active' : ''}`}>
                    <input type="radio" name="plan" value={p.id} checked={plan === p.id}
                      onChange={() => setPlan(p.id)} style={{ display: 'none' }} />
                    <span>
                      <span className="plan-opt-title">{planLabels[p.id].title}</span>
                      <span className="plan-opt-desc">{planLabels[p.id].desc}</span>
                    </span>
                    <span className="plan-opt-amount">
                      {amount != null ? formatIDR(amount) : t('pricing.setFromBrief')}
                      {p.id === 'dp50' && <span style={{ display: 'block', fontWeight: 500, fontSize: 10, color: 'var(--v2-color-taupe)' }}>{t('pricing.nowThen')} {formatIDR(modalPkg.price * 0.5)}</span>}
                    </span>
                  </label>
                );
              })}
            </div>

            {error && <p style={{ color: 'var(--v2-color-error)', fontSize: 12, margin: '0 0 12px' }}>{error}</p>}

            <button className="portal-btn portal-btn-primary portal-btn-block" onClick={confirmOrder} disabled={busy}>
              {busy ? t('pricing.settingUp') : user ? t('pricing.continueToDashboard') : t('pricing.signInWithGoogle')}
            </button>
            <p className="portal-muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 12 }}>
              {t('pricing.nextStep')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
